/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

// `init.entry` reaches this module synchronously to pick a bundle, so it has to stay
// clear of built-ins that `@babel/preset-env` polyfills: one core-js import here puts
// the shared vendor chunk on every page's critical path. Hence `./localePath` rather
// than `./locale`, and lookup objects rather than `Array#includes`. Membership is
// tested against `true` so inherited `Object.prototype` members can't pass for a path.
import { stripLocaleFromPath } from '@modules/i18n/localePath'

/** Paths served by the lightweight init/login bundle instead of the full app. */
const UNTRACKED_SHELL_PATHS: Record<string, true> = {
  '/logout': true,
  '/marketplace/aws/prepaid-subscription': true,
  '/marketplace/azure/prepaid-subscription': true,
}

const ANONYMOUS_SHELL_PATHS: Record<string, true> = {
  '/registration': true,
  '/serverless-registration': true,
  '/partner-signup': true,
  '/marketplace/registration/v2': true,
  '/marketplace-registration': true,
  '/marketplace/registration/v3': true,
  '/marketplace/login/v3': true,
  '/account/reset-password': true,
  '/account/verify-email': true,
  '/account/accept-invite': true,
  '/forgot': true,
  '/change-email': true,
  '/cloud-pricing-table': true,
  '/deployment-pricing-table': true,
  '/pricing': true,
  '/pricing/serverless': true,
}

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || `/`
}

/** Paths that boot the lightweight login bundle instead of the full app. */
export function isLoginShellPath(pathname: string): boolean {
  const path = normalizePathname(stripLocaleFromPath(pathname))

  if (path === `/login` || path.startsWith(`/login/`)) {
    return true
  }

  if (UNTRACKED_SHELL_PATHS[path] === true) {
    return true
  }

  if (ANONYMOUS_SHELL_PATHS[path] === true) {
    return true
  }

  return false
}
